import React, { useState } from "react";
import Grid from "@mui/material/Grid2";
import { Hidden, IconButton, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { AddBox } from "@mui/icons-material";
import MaterialTable from "material-table";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { defaultVariables } from "../../../../config";
import { TableCount, TablePage } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { tableIcons } from "../../../../config/design/TableIcons";
import { showTableDate } from "../../../../config/values";
import AppAnimate from "../../../components/AppAnimate";
import Title from "../../../components/elements/Title";
import LoadingButton from "../../../components/elements/LoadingButton";
import MakeMarketingRepButton from "./MakeMarketingRepButton";
import { CircleCheckBig, CircleX } from "lucide-react";

function MarketingRepsPage({ classes }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tableRef = React.useRef();
    const [inviteRep, setInviteRep] = useState(null);
    const [inviteLoading, setInviteLoading] = useState(false);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const reload = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    const toggleActive = async (id) => {
        try {
            await axios.post(`${defaultVariables.url}/marketing-representatives/toggle-active`, { id }, config);
            reload();
            enqueueSnackbar(t("detailsSet"), { variant: "success" });
        } catch (error) {
            enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
        }
    };

    const sendInviteSms = async () => {
        if (!inviteRep) return;

        setInviteLoading(true);
        try {
            await axios.post(`${defaultVariables.url}/marketing-representatives/send-invite-sms`, { id: inviteRep.id }, config);
            setInviteRep(null);
            enqueueSnackbar(t("marketingSendInviteSmsSuccess"), { variant: "success" });
        } catch (error) {
            enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
        } finally {
            setInviteLoading(false);
        }
    };

    const [columns] = useState([
        {
            title: t("fullName"),
            field: "fullName",
            editable: "never",
        },
        {
            title: t("mobile"),
            field: "mobile",
            editable: "never",
        },
        {
            title: t("marketingRegion"),
            field: "region",
            editable: "never",
        },
        {
            title: t("marketingLeadCount"),
            field: "vendor_leads_count",
            editable: "never",
        },
        {
            title: t("isActive"),
            field: "is_active",
            editable: "never",
            render: (rowData) =>
                !!rowData.is_active ? <CircleCheckBig className="lucide-icon-secondary" /> : <CircleX />,
        },
        {
            title: t("registerDate"),
            field: "created_at",
            editable: "never",
            render: (rowData) => (
                <Typography className={classes.dateTable}>{showTableDate(rowData.created_at)}</Typography>
            ),
        },
        {
            title: t("activity"),
            field: "activity",
            editable: "never",
            render: (rowData) => (
                <>
                    <MakeMarketingRepButton item_id={rowData.id} reload={reload} edit />
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        style={{ margin: 5 }}
                        onClick={() => setInviteRep(rowData)}
                    >
                        {t("marketingSendInviteSms")}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        style={{ margin: 5 }}
                        onClick={() => toggleActive(rowData.id)}
                    >
                        {rowData.is_active ? t("marketingDeactivate") : t("marketingActivate")}
                    </Button>
                </>
            ),
        },
    ]);

    return (
        <>
            <AppAnimate animation="transition.slideUpIn" delay={200} title={t("marketingRepsTitle")}>
                <Grid container spacing={2}>
                    <Grid item size={12}>
                        <Title align="left" color="text" className="panel-title">
                            {t("marketingRepsTitle")}
                        </Title>
                    </Grid>
                    <Grid item size={12} className="main-table">
                        <MaterialTable
                            tableRef={tableRef}
                            icons={tableIcons}
                            title={
                                <>
                                    <Hidden mdDown>
                                        <MakeMarketingRepButton reload={reload} />
                                    </Hidden>
                                    <Hidden mdUp>
                                        <MakeMarketingRepButton reload={reload} iconOnly />
                                    </Hidden>
                                </>
                            }
                            localization={getDataTableLocalization()}
                            columns={columns}
                            data={(query) =>
                                new Promise((resolve) => {
                                    axios
                                        .get(
                                            `${defaultVariables.url}/marketing-representatives/quickView?page=${query.page + 1}&per_page=${query.pageSize}&search=${query.search || ""}`,
                                            config
                                        )
                                        .then((response) => {
                                            resolve({
                                                data: response.data.data.map((row) => ({
                                                    ...row,
                                                    fullName: `${row.first_name} ${row.last_name}`,
                                                })),
                                                page: response.data.current_page - 1,
                                                totalCount: response.data.total,
                                            });
                                        })
                                        .catch(() => resolve({ data: [], page: 0, totalCount: 0 }));
                                })
                            }
                            options={{
                                pageSize: TablePage,
                                pageSizeOptions: TableCount,
                                sorting: false,
                                draggable: false,
                            }}
                        />
                    </Grid>
                </Grid>
            </AppAnimate>

            <Dialog open={!!inviteRep} onClose={() => !inviteLoading && setInviteRep(null)}>
                <DialogTitle>{t("marketingSendInviteSms")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("marketingSendInviteSmsConfirm", {
                            name: inviteRep ? `${inviteRep.first_name} ${inviteRep.last_name}` : "",
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button disabled={inviteLoading} onClick={() => setInviteRep(null)}>
                        {t("back")}
                    </Button>
                    <LoadingButton variant="contained" color="primary" loading={inviteLoading} onClick={sendInviteSms}>
                        {t("confirm")}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default withStyles(Styles)(MarketingRepsPage);
